import { useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { signIn, signOut, getCredits, logLogin } from '@/api/auth';
import { getAdminRole } from '@/lib/admin/roles';
import AstraeaAppShell from '@/components/AstraeaAppShell';

export default function AuthGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<number | null>(null);
  const [adminRole, setAdminRole] = useState<import('@/lib/admin/roles').AdminRole>('member');

  // 表单(仅登入,帐号由管理员开立,不开放自助注册)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 会员自助改密码
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<string | null>(null);

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwBusy(true);
    setPwMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPw });
      if (error) throw error;
      setPwMsg('密码已更新');
      setNewPw('');
      setTimeout(() => { setPwOpen(false); setPwMsg(null); }, 1200);
    } catch (e2) {
      setPwMsg(e2 instanceof Error ? e2.message : '更新失败');
    } finally {
      setPwBusy(false);
    }
  };

  const refreshBalance = useCallback(async () => {
    const b = await getCredits();
    setBalance(b);
  }, []);

  useEffect(() => {
    // 不能只看 session 是否存在:过期/被撤销的 session 仍会被 getSession 回传(读走 anon 仍可,
    // 但写入会 not authenticated)。这里用 getUser() 向服务器验证 token;无效就登出 → 显示登入表单,
    // 避免「看起来已登入、但所有写入都失败」的卡死状态。
    supabase.auth.getSession().then(async ({ data }) => {
      let s = data.session;
      if (s) {
        const { error } = await supabase.auth.getUser();
        if (error) { await supabase.auth.signOut(); s = null; }
      }
      setSession(s);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((evt, s) => {
      setSession(s);
      if (evt === 'SIGNED_IN' && s?.user) logLogin(s.user.email || '', s.user.id);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      refreshBalance();
      getAdminRole().then(setAdminRole);
    } else {
      setBalance(null);
      setAdminRole('member');
    }
  }, [session, refreshBalance]);

  useEffect(() => {
    const h = () => refreshBalance();
    window.addEventListener('credits-changed', h);
    return () => window.removeEventListener('credits-changed', h);
  }, [refreshBalance]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setNotice(null);
    try {
      await signIn(email.trim(), password);
    } catch {
      setErr('登入失败,请确认帐号密码(帐号由管理员开立)');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-6 h-6 border-2 border-secondary-200 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-background-50 to-background-100 px-4">
        <div className="w-full max-w-sm bg-background-50 rounded-2xl border border-secondary-200 shadow-sm p-7">
          <div className="text-center mb-6">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-primary-500 flex items-center justify-center">
              <i className="ri-sparkling-2-line text-background-50 text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">Astraea</h1>
            <p className="text-xs text-secondary-500 mt-1">AI 角色经营平台 · 会员登入</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-100 text-sm focus:outline-none focus:border-primary-400"
            />
            <input
              type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="密码(至少 6 位)"
              className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-100 text-sm focus:outline-none focus:border-primary-400"
            />
            {err && <div className="text-xs text-red-600"><i className="ri-error-warning-line mr-1"></i>{err}</div>}
            {notice && <div className="text-xs text-primary-600">{notice}</div>}
            <button
              type="submit" disabled={busy}
              className={`w-full py-2.5 rounded-lg text-sm font-semibold text-background-50 transition-colors ${busy ? 'bg-primary-400 cursor-wait' : 'bg-primary-500 hover:bg-primary-600'}`}
            >
              {busy ? '登入中…' : '登入'}
            </button>
          </form>
          <p className="mt-4 text-center text-[11px] text-secondary-400">帐号由管理员开立,如需帐号请联络管理员</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AstraeaAppShell
        adminRole={adminRole}
        balance={balance}
        email={session.user.email || ''}
        onChangePassword={() => { setPwOpen(true); setPwMsg(null); setNewPw(''); }}
        onSignOut={() => signOut()}
      >
        {children}
      </AstraeaAppShell>

      {pwOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" onClick={() => setPwOpen(false)}>
          <div className="w-full max-w-xs bg-background-50 rounded-2xl border border-secondary-200 shadow-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground-950">修改密码</h2>
              <button onClick={() => setPwOpen(false)} className="text-secondary-400 hover:text-foreground-700 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <form onSubmit={changePassword} className="space-y-3">
              <input
                type="password" required minLength={6} value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="新密码(至少 6 位)"
                className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-100 text-sm focus:outline-none focus:border-primary-400"
              />
              {pwMsg && <div className="text-xs text-primary-600">{pwMsg}</div>}
              <button type="submit" disabled={pwBusy} className={`w-full py-2.5 rounded-lg text-sm font-semibold text-background-50 ${pwBusy ? 'bg-primary-400 cursor-wait' : 'bg-primary-500 hover:bg-primary-600'}`}>
                {pwBusy ? '更新中…' : '更新密码'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
